import type { ActiveOrderFlag } from '../orderFlags.schema';

export function normalizeEmail(email: string | null | undefined): string | null {
    if (!email) return null;
    const trimmed = email.trim().toLowerCase();
    return trimmed || null;
}

// Reduce a phone to its national significant digits so formatting variants
// (spaces, +40, 0040, leading 0) compare equal. Mirror of normalizePhone() in
// api/modules/order-flags/services/order-flag.service.js — keep in lockstep.
export function normalizePhone(phone: string | null | undefined): string | null {
    if (!phone) return null;
    let digits = String(phone).replace(/\D/g, '');
    if (digits.startsWith('0040')) digits = digits.slice(4);
    else if (digits.startsWith('40') && digits.length > 9) digits = digits.slice(2);
    if (digits.startsWith('0')) digits = digits.slice(1);
    return digits || null;
}

/**
 * Tags matching an order's contact info, deduped by tag code. An order matches
 * an entry when its email OR any of its phones hits that entry (either alone is
 * enough). `phone` accepts a single value or several (e.g. billing + delivery).
 */
export function matchOrderFlags(
    flags: ActiveOrderFlag[] | undefined,
    email: string | null | undefined,
    phone: string | null | undefined | Array<string | null | undefined>,
): ActiveOrderFlag['tag'][] {
    if (!flags || flags.length === 0) return [];
    const e = normalizeEmail(email);
    const phones = (Array.isArray(phone) ? phone : [phone])
        .map(normalizePhone)
        .filter((p): p is string => !!p);
    const phoneSet = new Set(phones);
    if (!e && phoneSet.size === 0) return [];

    const seen = new Set<string>();
    const out: ActiveOrderFlag['tag'][] = [];
    for (const flag of flags) {
        const emailHit = !!e && !!flag.emailNormalized && flag.emailNormalized === e;
        const phoneHit = !!flag.phoneNormalized && phoneSet.has(flag.phoneNormalized);
        if ((emailHit || phoneHit) && !seen.has(flag.tag.code)) {
            seen.add(flag.tag.code);
            out.push(flag.tag);
        }
    }
    return out;
}
